<template>
  <b-modal-mission-wizard
    v-model="model"
    :total-steps="wizard.TOTAL_STEPS"
    :total-visible-steps="wizard.TOTAL_VISIBLE_STEPS"
    :current-step="wizard.currentStep.value"
    :title="currentTitle"
    :description="wizard.currentDescription.value"
    :can-go-next="wizard.canGoNext.value"
    :loading="wizard.loading.value"
    :next-button-text="wizard.nextButtonText.value"
    :back-button-text="$t('common.back')"
    :stepper-visible-steps="wizard.STEPPER_VISIBLE_STEPS"
    :hide-header-steps="wizard.HIDE_HEADER_STEPS"
    :hide-footer-steps="wizard.HIDE_FOOTER_STEPS"
    :show-back-button-steps="wizard.SHOW_BACK_BUTTON_STEPS"
    :content-classes-per-step="{ 5: 'px-0 py-0' }"
    data-e2e="publish-mission-modal"
    @update:current-step="wizard.currentStep.value = $event"
    @next="handleNext"
    @back="wizard.goBack"
  >
    <template #steps="{ currentStep }">
      <!-- In edit mode, delay mounting until mission data is loaded (createdMission set).
           This ensures the immediate watchers in step components see the data on first render.
           In create mode, mount immediately (createdMission is null). -->
      <template v-if="wizard.action.value !== 'update' || wizard.createdMission.value">
        <b-publish-mission-step1
          v-show="currentStep === 0"
          @change="wizard.onStep1Change"
          :initial-data="wizard.stepData.value.step1"
          data-e2e="wizard-step-1"
        />
        <b-publish-mission-step2
          v-show="currentStep === 1"
          @change="wizard.onStep2Change"
          :initial-data="wizard.stepData.value.step2"
          data-e2e="wizard-step-2"
        />
        <b-publish-mission-step3
          v-show="currentStep === 2"
          @change="wizard.onStep3Change"
          :initial-data="wizard.stepData.value.step3"
          data-e2e="wizard-step-3"
        />
      </template>
      <b-publish-mission-step4
        v-show="currentStep === 3"
        :mission="wizard.createdMission.value"
        data-e2e="wizard-step-4"
      />
      <b-publish-mission-step5
        v-show="currentStep === 4"
        :mission="wizard.createdMission.value"
        v-model:selected-experts="wizard.selectedExperts.value"
        data-e2e="wizard-step-5"
      />
      <b-publish-mission-step6
        v-show="currentStep === 5"
        :mission="wizard.createdMission.value"
        :selected-experts="wizard.selectedExperts.value"
        @finish="handleFinish"
        data-e2e="wizard-step-6"
      />
    </template>

    <template #footer="{ currentStep, goBack, goNext, loading }">
      <!-- Step 4 specific layout: two equal-width buttons side by side -->
      <template v-if="currentStep === 3">
        <b-button
          light
          block
          @click="wizard.onSkipExpertSearch"
          data-e2e="wizard-skip-button"
        >
          {{ $t('publish_mission.later') }}
        </b-button>
        <b-button
          block
          :loading="loading"
          @click="goNext"
          data-e2e="wizard-next-button"
        >
          {{ $t('publish_mission.now') }}
        </b-button>
      </template>

      <!-- Default layout for other steps -->
      <template v-else-if="currentStep !== 5">
        <b-button
          :loading="loading"
          :disabled="!wizard.canGoNext.value"
          block
          class="w-full"
          @click="goNext"
          data-e2e="wizard-next-button"
        >
          {{ wizard.nextButtonText.value }}
        </b-button>
      </template>
    </template>
  </b-modal-mission-wizard>

  <!-- Save Draft Modal -->
  <b-save-draft-modal
    v-model="wizard.showSaveDraftModal.value"
    @save="wizard.onSaveDraft"
    @discard="wizard.onDiscardDraft"
  />
</template>

<script setup lang="ts">
import { watch, ref, computed, nextTick } from 'vue';
import BModalMissionWizard from '~/components/modal/mission/BModalMissionWizard.vue';
import BButton from '~/components/forms/BButton.vue';
import BPublishMissionStep1 from '~/components/company/missions/wizard/BPublishMissionStep1.vue';
import BPublishMissionStep2 from '~/components/company/missions/wizard/BPublishMissionStep2.vue';
import BPublishMissionStep3 from '~/components/company/missions/wizard/BPublishMissionStep3.vue';
import BPublishMissionStep4 from '~/components/company/missions/wizard/BPublishMissionStep4.vue';
import BPublishMissionStep5 from '~/components/company/missions/wizard/BPublishMissionStep5.vue';
import BPublishMissionStep6 from '~/components/company/missions/wizard/BPublishMissionStep6.vue';
import BSaveDraftModal from '~/components/company/missions/wizard/BSaveDraftModal.vue';
import { useCompanyMissionWizard } from '~/composables/mission/useCompanyMissionWizard';
import type { MissionPrefillData } from '~/composables/usePublishMissionModal';
import { missionStore } from '~/stores/mission';
import { useCompanyStore } from '~/stores/companyStore';
import { DEFAULT_LANGUAGES } from '~/help/languages';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const publishMissionModal = usePublishMissionModal();
const storeMission = missionStore();
const companyStore = useCompanyStore();

// Track reset timeout to cancel if modal reopens quickly
const resetTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

const model = defineModel<boolean>({ default: false });
const emit = defineEmits(['mission-created']);

// Initialize wizard with callbacks
const wizard = useCompanyMissionWizard({
  onMissionCreated: async () => {
    await publishMissionModal.triggerMissionCreated();
    emit('mission-created');
  },
  onClose: () => {
    model.value = false;
  },
});

// Dynamic title based on action (create vs edit)
const currentTitle = computed(() => {
  if (wizard.action.value === 'update') {
    return t('publish_mission.edit_modal_title');
  }
  return wizard.currentTitle.value;
});

// Handle next button click
const handleNext = async () => {
  const success = await wizard.onNext();

  if (success && wizard.currentStep.value === 6) {
    // Wizard completed (create mode)
    model.value = false;
    router.push('/company/missions');
  }
};

// Handle finish from step 6
const handleFinish = async () => {
  await wizard.onFinish();
  // Final refresh to ensure table shows latest state (invitations, status, etc.)
  await publishMissionModal.triggerMissionCreated();
  model.value = false;
  router.push('/company/missions');
};

// Fetch mission details for edit mode
const fetchMissionDetails = async (missionId: string) => {
  if (!missionId) return;

  try {
    const mission = await storeMission.fetchOneMission(missionId, { includes: 'company,commission' });
    const isOwnCompany = mission.company?.company_id === companyStore.company?.company_id;

    wizard.stepData.value.step1 = {
      use_own_company: isOwnCompany,
      company_type: mission.company_size?.company_size_id || undefined,
      industry: mission.industry?.industry_id || undefined,
      industry_label: mission.industry?.name || undefined,
      personas: mission.personas?.map((p: { persona_id: string; name: string }) => ({
        value: p.persona_id,
        label: p.name,
      })) || [],
      deal_sizes: mission.deal_sizes?.map((d: { deal_size_id: string }) => d.deal_size_id) || [],
      market_segments: mission.market_segments?.map((m: { market_segment_id: string }) => m.market_segment_id) || [],
      siren: !isOwnCompany ? (mission.company_siren || mission.company?.siren) : undefined,
      company_label: !isOwnCompany ? (mission.company_name || mission.company?.business_name) : undefined,
      contact_email: !isOwnCompany ? mission.company?.contact?.email : undefined,
      contact_email_readonly: !isOwnCompany && !!mission.company?.contact?.email,
    };

    wizard.stepData.value.step2 = {
      job_position: mission.title || '',
      description: mission.description || '',
      skills: mission.skills?.map((s: { skill_id: string; name: string }) => ({
        value: s.skill_id,
        label: s.name,
      })) || [],
    };

    wizard.stepData.value.step3 = {
      budget: mission.budget || undefined,
      work_location: mission.mobility_type || undefined,
      is_public: mission.is_public ?? true,
      languages: mission.languages && mission.languages.length > 0 ? mission.languages : [...DEFAULT_LANGUAGES],
    };

    // Set createdMission LAST — this triggers v-if on step components,
    // which then mount with stepData already populated
    wizard.createdMission.value = mission;
  } catch (error) {
    console.error('Error fetching mission details:', error);
  }
};

// Fetch mission details for clone mode - pre-fills wizard in CREATE mode (no dates, no budget)
const fetchMissionDetailsForClone = async (missionId: string) => {
  if (!missionId) return;

  try {
    const mission = await storeMission.fetchOneMission(missionId, { includes: 'company,commission' });
    const isOwnCompany = mission.company?.company_id === companyStore.company?.company_id;

    // Pre-fill step 1: target market (same as edit, but stays in create mode)
    wizard.stepData.value.step1 = {
      use_own_company: isOwnCompany,
      company_type: mission.company_size?.company_size_id || undefined,
      industry: mission.industry?.industry_id || undefined,
      industry_label: mission.industry?.name || undefined,
      personas: mission.personas?.map((p: { persona_id: string; name: string }) => ({
        value: p.persona_id,
        label: p.name,
      })) || [],
      deal_sizes: mission.deal_sizes?.map((d: { deal_size_id: string }) => d.deal_size_id) || [],
      market_segments: mission.market_segments?.map((m: { market_segment_id: string }) => m.market_segment_id) || [],
      siren: !isOwnCompany ? (mission.company_siren || mission.company?.siren) : undefined,
      company_label: !isOwnCompany ? (mission.company_name || mission.company?.business_name) : undefined,
      contact_email: !isOwnCompany ? mission.company?.contact?.email : undefined,
      contact_email_readonly: !isOwnCompany && !!mission.company?.contact?.email,
    };

    // Pre-fill step 2: expert profile (no title — user must set a new one)
    wizard.stepData.value.step2 = {
      description: mission.description || '',
      skills: mission.skills?.map((s: { skill_id: string; name: string }) => ({
        value: s.skill_id,
        label: s.name,
      })) || [],
    };

    // Pre-fill step 3: conditions: NO budget, NO dates (per spec). Required
    // languages describe the expert being sought, so carry them over like skills.
    wizard.stepData.value.step3 = {
      work_location: mission.mobility_type || undefined,
      is_public: mission.is_public ?? true,
      languages: mission.languages && mission.languages.length > 0 ? mission.languages : [...DEFAULT_LANGUAGES],
    };

    // Stay in create mode (action remains 'create'), start at step 0
    wizard.currentStep.value = 0;

    // Flag as restoring to let step components process the data
    wizard.isRestoringDraft.value = true;
    await nextTick();
    wizard.isRestoringDraft.value = false;
  } catch (error) {
    console.error('Error fetching mission details for clone:', error);
  }
};

// Apply prefill data coming from the expert search filters.
// Stays in create mode (action remains 'create'), starts at step 0.
// Overwrites any restored draft because we never call restoreDraftFromStore in this branch.
const applyPrefill = async (prefill: MissionPrefillData) => {
  wizard.stepData.value.step1 = {
    use_own_company: true,
    ...(prefill.step1 || {}),
  };
  wizard.stepData.value.step2 = {
    ...(prefill.step2 || {}),
  };
  wizard.stepData.value.step3 = {
    ...(prefill.languages ? { languages: prefill.languages } : {}),
  };

  wizard.action.value = 'create';
  wizard.currentStep.value = 0;

  // Flag as restoring so the immediate watchers in step components process the data
  wizard.isRestoringDraft.value = true;
  await nextTick();
  wizard.isRestoringDraft.value = false;
};

// Initialize company data when modal opens, reset when closes
watch(model, async (isOpen) => {
  if (isOpen) {
    // Cancel any pending reset from previous close
    if (resetTimeoutId.value !== null) {
      clearTimeout(resetTimeoutId.value);
      resetTimeoutId.value = null;
    }

    // Capture query params BEFORE any async operation — they may be cleared
    // by navigation or other watchers during the await
    const editMissionId = route.query.mission as string | undefined;
    const isEditMode = wizard.action.value === 'update';

    await wizard.initialize();

    // Check if modal was opened with specific options (e.g. jump to expert search for existing mission)
    const openOptions = publishMissionModal.consumeOpenOptions();
    if (openOptions?.prefill) {
      // Prefill mode: open in create mode with data coming from the expert search filters
      await applyPrefill(openOptions.prefill);
    } else if (openOptions?.action === 'clone' && openOptions.missionId) {
      // Clone mode: fetch source mission and pre-fill stepData (without dates/budget)
      await fetchMissionDetailsForClone(openOptions.missionId);
    } else if (openOptions?.missionId) {
      await fetchMissionDetails(openOptions.missionId);
      if (openOptions.step !== undefined) {
        wizard.currentStep.value = openOptions.step;
      }
    } else if (isEditMode && editMissionId) {
      await fetchMissionDetails(editMissionId);
    } else {
      // Restore draft after initialization (only in create mode, when not opened with options)
      wizard.restoreDraftFromStore();
    }
  } else {
    // If a mission was created (step >= 3), trigger final refresh on close
    // This handles X button or click-outside close scenarios
    if (wizard.createdMission.value !== null) {
      await publishMissionModal.triggerMissionCreated();
    }

    // Save draft silently before reset (draft mode feature, only in create mode)
    if (wizard.action.value !== 'update') {
      wizard.saveDraftOnClose();
    }
    // Delay reset to allow closing animation
    resetTimeoutId.value = setTimeout(() => {
      wizard.reset();
      resetTimeoutId.value = null;
    }, 300);

    // Clean up URL query parameters
    const query = { ...route.query };
    delete query.mission;
    delete query.action;
    router.replace({ path: route.path, query });
  }
});

// URL query parameter handling for edit mode
// Only sets action + opens modal — fetchMissionDetails is called from watch(model)
// after wizard.initialize() completes, ensuring companyStore is loaded
watch(() => route.query, (newValue) => {
  if (newValue.mission && newValue.action === 'update') {
    wizard.action.value = 'update';
    model.value = true;
  }
}, { immediate: true });
</script>
